import React, { useMemo } from 'react';
import { message, Tabs } from 'antd';
import { useThrottleEffect } from 'ahooks';
import { EcoState, useDispatch, useStore } from '../context';
import Coupon from '../Coupon2';
import NoteModal from '../ModalComp/NoteModal';
import BuyForm from './BuyForm';
import css from './index.module.scss';
import { copyText } from '@/utils';
import { useGlobal } from '@/utils/useGlobal';
import Button from '@/components/UI/Button';
import EfModal from '@/components/UI/Modal';
import { EFArrow } from '@/components/UI/svg/Search';
import { rewardRedeemCoupon } from '@/service';
import { signupCookie } from '@/utils/cookies/signupCookie';

interface props {
    isLogin: boolean;
    rewardUserInfo: any;
    modal?: boolean;
    viewAllStr?: string;
    title?: string;
    subtitle?: string;
    refershData?: () => void;
    setShowPage: (modal: string) => void;
    defauleActiveTab?: string;
    pathUrl?: string;
    isSimplify?: boolean;
}

interface ModuleTab {
    key: keyof EcoState;
    label: string;
}

const EcoCreditRules: React.FC<props> = ({
    rewardUserInfo,
    modal,
    viewAllStr,
    title,
    subtitle,
    refershData,
    isLogin,
    setShowPage,
    defauleActiveTab = 'COUPONS_RULES',
    pathUrl,
    isSimplify = false,
}) => {
    const { useState, useRef, useRouter, useLang, isMobile, Link } = useGlobal();
    const { t } = useLang('ecocredits');
    const state = useStore();
    const dispatch = useDispatch();
    const buyFormRef = useRef(null);
    const router = useRouter();
    const { locale, asPath } = router;
    const href = typeof window === 'undefined' ? '' : window.location.href;

    // 各种弹框
    const [couponModalOpen, setCouponModalOpen] = useState(false);
    const [currCoupon, setCurrCoupon] = useState({});
    const [currRules, setCurrRules] = useState(null);
    const [reedingLoading, setReedingLoading] = useState(false);
    const [currNotes, setCurrNotes] = useState(null);
    const [activeKey, setActiveKey] = useState(defauleActiveTab);

    const allTabs: ModuleTab[] = pathUrl
        ? [
              { key: 'COUPONS_RULES_URL', label: t('discount') },
              { key: 'FREE_PRODUCTS_RULES_URL', label: t('product_text') },
              { key: 'THIRD_COUPONS_RULES_URL', label: t('gift_card') },
          ]
        : [
              { key: 'COUPONS_RULES', label: t('discount') },
              { key: 'FREE_PRODUCTS_RULES', label: t('product_text') },
              { key: 'THIRD_COUPONS_RULES', label: t('gift_card') },
          ];

    const filterTabs = useMemo(() => {
        return allTabs?.filter((tab) => {
            return state[tab.key]?.length;
        });
    }, [state]);

    const resetRewardRedeemRules = useMemo(() => {
        const activeList = state[activeKey as keyof EcoState];
        return activeList?.slice(0, modal ? activeList?.length : isMobile ? 4 : 8);
    }, [state, modal, isMobile, activeKey]);

    const successBtnMap: any = {
        COUPONS: t('store'),
        THIRD_COUPONS: t('spend_now'),
    };

    const viewAll = () => {
        pathUrl ? setShowPage?.(`ecocredit_rules_next$${activeKey}`) : setShowPage?.(`points_modal$${activeKey}`);
    };

    const openConfirmModal = async (rule: any) => {
        if (!isLogin) {
            signupCookie?.({
                sector_name: title ? title : t('redeem_ecocredits_title'),
                entry_position: 5,
            });
            router.push(`/${locale}/login?redirect=${encodeURIComponent(`/${locale}/${asPath}#ECOCREDIT_RULES`)}`);
            return;
        }
        setCurrRules(rule);
    };

    const onConfirm = async () => {
        await onRedeem(currRules);
        setCurrRules(null);
    };

    const onRedeem = async (rule: any) => {
        if (!isLogin) {
            signupCookie?.({
                sector_name: title ? title : t('redeem_ecocredits_title'),
                entry_position: 5,
            });
            router.push(`/${locale}/login?redirect=${encodeURIComponent(`/${locale}/${asPath}#ECOCREDIT_RULES`)}`);
            return;
        }

        const { ruleId, version, ruleWebsite, ruleType } = rule;
        const ruleSiteVersion = ruleWebsite?.version;
        const params: any = { ruleId, ruleVersion: version, ruleSiteVersion };

        // 如果是campaign规则 加上campaignId
        if (rule?.campaignId) {
            params.campaignVersion = rule?.campaignVersion;
        }

        // 如果是实体产品 加上地址
        if (ruleType === 'FREE_PRODUCTS') {
            const address = await buyFormRef?.current?.getFormData();
            params.address = address;
        }
        if (reedingLoading) return;
        setReedingLoading(true);

        try {
            const res = (await rewardRedeemCoupon(params, locale)) || '';
            if (res?.code === '0') {
                const data = res?.data || {};
                setCouponModalOpen(true);
                setCurrCoupon({ ...rule, ...data });
                copyText(res?.data?.coupon || '');
                refershData && refershData();
                dispatch({
                    type: 'getCouponsRules',
                    payload: { t, locale, pathUrl: pathUrl },
                });
                dispatch({
                    type: 'getProductRules',
                    payload: { locale, pathUrl },
                });
                dispatch({
                    type: 'getThiredCouponRules',
                    payload: { locale, pathUrl },
                });
                return;
            }

            // 积分不足
            if (res?.code === '2418') {
                message.error(t('no_enough_points') || res?.message);
                return;
            }

            // 前端页面长时间不刷新，导致兑换规则落后后端版本
            if (res?.code === '2422') {
                message.error(t('stay_too_long') || res?.message);
                return;
            }

            // 兑换次数超出上限
            if (res?.code === '2423') {
                message.error(t('exceeded_counts') || res?.message);
                return;
            }

            // 前端页面长时间不刷新，导致优惠券规则超过截止时间
            if (res?.code === '2424') {
                message.error(t('stay_too_long') || res?.message);
                return;
            }

            // 库存不足
            if (res?.code === '2429') {
                message.error(t('no_stock_tip') || res?.message);
                return;
            }

            // 库存不足
            if (res?.code === '2426') {
                message.error(t('no_stock_tip') || res?.message);
                return;
            }

            message.error(res?.message || t('network_error') || 'Network Error');
        } finally {
            setReedingLoading(false);
        }
    };

    const shopNow = (coupon: any) => {
        const { thirdUrl, ruleType, jumpUrl } = coupon;
        // 第三方
        if (ruleType === 'THIRD_COUPONS') {
            if (isMobile) {
                router.push(thirdUrl);
                return;
            }
            setTimeout(() => window.open(thirdUrl, '_blank'));
            return;
        }

        // 普通优惠券并且填了jumpUrl
        if (ruleType === 'COUPONS' && jumpUrl) {
            if (isMobile) {
                router.push(jumpUrl);
                return;
            }
            setTimeout(() => window.open(jumpUrl, '_blank'));
            return;
        }

        // 默认
        if (isMobile) {
            router.push(`https://${locale}.ecoflow.com`);
            return;
        }
        window.open(`https://${locale}.ecoflow.com`, '_blank');
    };

    const getShowBtn = (coupon: any) => {
        const { ruleType, thirdUrl } = coupon || {};

        if (ruleType === 'COUPONS') return true;

        if (ruleType === 'THIRD_COUPONS' && thirdUrl) return true;

        return false;
    };

    useThrottleEffect(() => {
        dispatch({
            type: 'getCouponsRules',
            payload: { t, locale, pathUrl },
        });
        dispatch({
            type: 'getProductRules',
            payload: { locale, pathUrl },
        });
        dispatch({
            type: 'getThiredCouponRules',
            payload: { locale, pathUrl },
        });
        dispatch({
            type: 'checkBlackList',
            payload: { locale },
        });
    }, []);

    useThrottleEffect(() => {
        if (!filterTabs?.length) return;
        if (resetRewardRedeemRules?.length) return;
        setActiveKey(filterTabs[0].key);
    }, [filterTabs?.length]);

    if (!filterTabs?.length) return <div></div>;

    return (
        <>
            <div className={`${css.ecocredit_rules} ${modal ? 'modal' : ''} ${isSimplify ? 'simplify' : ''}`}>
                {!isSimplify && (
                    <>
                        <h5 id="FOR-REDEEM-NOW" className={`credits-title ${isLogin ? 'login' : 'unLogin'}`}>
                            {title ? title : t('redeem_ecocredits_title')}
                        </h5>
                        {subtitle && <p className="credits-subtitle">{subtitle}</p>}
                        <div className="container-center">
                            {filterTabs?.length > 1 && (
                                // <Tabs
                                //     activeKey={activeKey}
                                //     className="rewards-tabs"
                                //     onChange={setActiveKey}
                                //     centered
                                //     items={filterTabs?.map((item) => ({ label: item.label, key: item.key }))}
                                // />
                                <Tabs activeKey={activeKey} className="rewards-tabs" onChange={setActiveKey} centered>
                                    {filterTabs?.map((tab) => (
                                        <Tabs.TabPane tab={tab.label} key={tab.key}></Tabs.TabPane>
                                    ))}
                                </Tabs>
                            )}
                            <div className="credits-desc-container">
                                <span className="credits-desc">
                                    {isLogin &&
                                        t.rich('have_points', {
                                            code: (children: string) => <i className="points">{children}</i>,
                                            points: rewardUserInfo?.points || '0',
                                        })}
                                </span>
                                <span style={{ display: modal ? 'none' : 'block' }} onClick={viewAll} className="view-all">
                                    <span className="viewall-text">{t('view_all')}</span>
                                    <EFArrow />
                                </span>
                            </div>
                        </div>
                    </>
                )}

                <div className="rule-content">
                    <div className="rule-list-wrap">
                        <ul className="rule-list">
                            {resetRewardRedeemRules?.map((rule: any) => (
                                <Coupon
                                    disabledBtn={
                                        state.inBlackList ||
                                        (isLogin && Number(rule?.points) > rewardUserInfo?.points) ||
                                        (rule?.ruleType !== 'COUPONS' && rule?.stock === 0)
                                    }
                                    showstock={rule?.ruleType !== 'COUPONS' && rule?.stockPicEnable}
                                    className="rule-item"
                                    key={rule?.ruleId}
                                    coupon={rule}
                                    onViewsRules={setCurrNotes}
                                    onReedem={openConfirmModal}
                                    showCountDown
                                    hideAmount={!['COUPONS', 'GIFT_CARD']?.includes(rule?.ruleType)}
                                />
                            ))}
                        </ul>
                        {isSimplify ? (
                            <a href="#ECOCREDIT_RULES">
                                <p style={{ display: modal ? 'none' : 'block' }} className="view-all-bottom">
                                    <span className="viewall-text-bottom">{t('view_more')}</span>
                                    <EFArrow />
                                </p>
                            </a>
                        ) : (
                            <p style={{ display: modal ? 'none' : 'block' }} onClick={viewAll} className="view-all-bottom">
                                <span className="viewall-text-bottom">{viewAllStr || t('view_all')}</span>
                                <EFArrow />
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* 兑换成功弹框 */}
            <EfModal
                className={`${css.efmodal} coupon`}
                open={couponModalOpen}
                visible={couponModalOpen}
                onCancel={() => setCouponModalOpen(false)}
                zIndex={1200}
            >
                <div className="content">
                    <Coupon
                        className="coupon-card"
                        coupon={currCoupon}
                        onViewsRules={setCurrNotes}
                        hideAmount={!['COUPONS', 'GIFT_CARD']?.includes(currCoupon?.ruleType)}
                        showJumpBtn={'THIRD_COUPONS' === currCoupon?.ruleType && currCoupon.useUrl}
                        hideBtn
                        hideNote
                    />
                    <p className="m-desc">{t('redeemed_succ')}</p>
                    {['FREE_PRODUCTS', 'THIRD_COUPONS']?.includes(currCoupon?.ruleType) ? (
                        <p className="m-third-desc">
                            {currCoupon?.ruleType === 'THIRD_COUPONS' && `${t('copy_desc_2')}, `}
                            <br />
                            {t.rich('grab_success_text2', {
                                code: (children: string) => (
                                    <i
                                        className="btn"
                                        onClick={() => {
                                            setCouponModalOpen(false);
                                            setShowPage('coupon_modal$1');
                                            setCurrCoupon(undefined);
                                        }}
                                    >
                                        {children}
                                    </i>
                                ),
                            })}
                        </p>
                    ) : (
                        <p className="m-desc end">{t('copy_desc')}</p>
                    )}

                    {getShowBtn(currCoupon) && (
                        <Button onClick={() => shopNow(currCoupon)} className="m-btn">
                            {successBtnMap[currCoupon?.ruleType] || t('store')}
                        </Button>
                    )}
                </div>
            </EfModal>

            {/* 兑换确认弹框 */}
            <EfModal className={`${css.efmodal} coupon over-height`} open={!!currRules} visible={!!currRules} onCancel={() => setCurrRules(null)} zIndex={1200}>
                <div className="content">
                    <div className="prompt_icon">
                        <Coupon
                            className="coupon-card"
                            coupon={currRules}
                            hideAmount={!['COUPONS', 'GIFT_CARD']?.includes(currRules?.ruleType)}
                            hideBtn
                            hideNote
                        />
                    </div>
                    <div className="confirm-rule-content">
                        <h5 className="confirm-rule-title">{t('rules')}</h5>
                        <ul className="confirm-rule-list">
                            {currRules?.noteList?.map((note: string, index: number) => (
                                <li key={index} className="confirm-rule-item">
                                    {note}
                                </li>
                            ))}
                        </ul>
                    </div>
                    {currRules?.ruleType === 'FREE_PRODUCTS' && <BuyForm ref={buyFormRef} />}
                    <h5 className="m-prompt-title">{t('confirm_text')}</h5>
                    <Button loading={reedingLoading} onClick={onConfirm} className="m-btn">
                        {t('confirm_btn')}
                    </Button>
                </div>
            </EfModal>

            {/* 优惠券rules弹框 */}
            <NoteModal currNotes={currNotes} setCurrNotes={setCurrNotes} />
        </>
    );
};

export default EcoCreditRules;
